import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { DataGrid, GridFilterInputDate } from "@mui/x-data-grid";
import { Typography, Box, CircularProgress, Alert, Tooltip, Button, TextField, Autocomplete, Checkbox } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";

import { useCurrentUser } from '../hooks/useCurrentUser';
import { fetchDirectReportsByPmId, fetchManagedUsersHierarchy, mapHierarchyUserForSearch } from '../utils/absenceCalendarExport';
import { sortUsersByDisplayName, sortUsersGroupedByPeopleManager, getPeopleManagerGroupName } from '../utils/peopleManagerApi';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import { excludeForecastWorklogs } from '../utils/worklogFilters';


/** Serializza i filtri colonna DataGrid per l'API worklogs (server-side). */
function serializeGridFilters(filterModel) {
  const items = (filterModel?.items || []).filter((item) => {
    if (!item?.operator) return false;
    if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') return true;
    if (item.value == null) return false;
    if (typeof item.value === 'string') return item.value.trim() !== '';
    if (Array.isArray(item.value)) return item.value.length > 0;
    return true;
  });
  if (items.length === 0) return null;
  return JSON.stringify(
    items.map(({ field, operator, value }) => ({ field, operator, value }))
  );
}

function createPeopleManagerWorklogColumns() {
  return [
    { field: 'people_manager_name', headerName: 'People Manager', width: 180 },
    { field: 'user_name', headerName: 'User', width: 180 },
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'team_name', headerName: 'Team', width: 160 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      type: 'date',
      filterable: true,
      valueFormatter: (value) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) > new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) < new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort (min)', 
      width: 100,
      valueFormatter: (value) => {
        if (!value) return '';
        const minutes = Number(value);
        return `${minutes}`;
      }
    },
    { 
      field: 'effort_hours', 
      headerName: 'Effort (hours)', 
      width: 120,
      valueGetter: (value, row) => {
        return row.effort;
      },
      valueFormatter: (value) => {
        if (!value) return '';
        const minutes = Number(value);
        const hours = Math.round((minutes / 60) * 100) / 100;
        return `${hours}`;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 180 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    { field: 'description', headerName: 'Description', width: 250 },
  ];
}

export default function TrackingListPeopleManagerView() {
  const { userId, userName } = useCurrentUser();
  
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [rowCount, setRowCount] = useState(0);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [gridSortModel, setGridSortModel] = useState([{ field: 'date', sort: 'desc' }]);
  const [managedUsers, setManagedUsers] = useState([]); // Solo report diretti
  const [hierarchyUsers, setHierarchyUsers] = useState([]);
  const [hierarchyUsersLoading, setHierarchyUsersLoading] = useState(false);
  const [hierarchyLoadError, setHierarchyLoadError] = useState(null);
  
  // Refs per evitare chiamate duplicate
  const isFetchingUsers = useRef(false);
  const isFetchingWorklogs = useRef(false);
  const hasAutoSetFilterUsers = useRef(false);
  const isFetchingHierarchy = useRef(false);
  const hierarchyFetched = useRef(false);
  const hierarchyDataRef = useRef([]);
  const hierarchyLoadPromiseRef = useRef(null);

  // Filtri per utente gestito e intervallo date
  const [filterUsers, setFilterUsers] = useState([]);
  /** direct = solo report diretti; hierarchy = tutta la gerarchia; custom = selezione esplicita */
  const [userFilterMode, setUserFilterMode] = useState('direct');
  const [pendingUserFilterMode, setPendingUserFilterMode] = useState('direct');
  // Imposta di default il mese corrente
  function getCurrentMonthRange() {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // yyyy-mm-dd
    const toStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: toStr(firstDayOfMonth), end: toStr(lastDayOfMonth) };
  }
  const currentMonth = getCurrentMonthRange();
  const [filterStartDate, setFilterStartDate] = useState(currentMonth.start);
  const [filterEndDate, setFilterEndDate] = useState(currentMonth.end);

  // Stato per i filtri "pending" (modificabili dall'utente)
  const [pendingFilterUsers, setPendingFilterUsers] = useState(filterUsers);
  const [pendingFilterStartDate, setPendingFilterStartDate] = useState(filterStartDate);
  const [pendingFilterEndDate, setPendingFilterEndDate] = useState(filterEndDate);

  const columns = useMemo(() => createPeopleManagerWorklogColumns(), []);

  // Aggiorna i filtri effettivi solo quando si preme il pulsante
  const applyFilters = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setGridFilterModel({ items: [] });
    setGridSortModel([{ field: 'date', sort: 'desc' }]);
    setUserFilterMode(pendingUserFilterMode);
    setFilterUsers(pendingFilterUsers);
    setFilterStartDate(pendingFilterStartDate);
    setFilterEndDate(pendingFilterEndDate);
  };

  const handleMainGridPaginationChange = (model) => {
    setPaginationModel((prev) => ({
      page: model?.page ?? prev.page,
      pageSize: model?.pageSize ?? prev.pageSize,
    }));
  };

  const handleMainGridFilterChange = (model) => {
    setGridFilterModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleMainGridSortChange = (model) => {
    setGridSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const selectAllHierarchyPending = async () => {
    if (pendingUserFilterMode === 'hierarchy') {
      setPendingUserFilterMode('direct');
      setPendingFilterUsers([]);
      return;
    }
    try {
      setHierarchyLoadError(null);
      await ensureHierarchyLoaded();
      setPendingUserFilterMode('hierarchy');
      setPendingFilterUsers([]);
    } catch {
      setHierarchyLoadError('Unable to load the full team hierarchy. Direct reports are still available.');
    }
  };

  const ensureHierarchyLoaded = useCallback(async () => {
    if (!userId) return [];
    if (hierarchyFetched.current) {
      return hierarchyDataRef.current;
    }
    if (hierarchyLoadPromiseRef.current) {
      return hierarchyLoadPromiseRef.current;
    }

    isFetchingHierarchy.current = true;
    setHierarchyUsersLoading(true);
    setHierarchyLoadError(null);

    const promise = fetchManagedUsersHierarchy(userId, userName || 'Direct reports')
      .then((users) => {
        const formatted = sortUsersGroupedByPeopleManager(
          users.map((user) => mapHierarchyUserForSearch(user, userId))
        );
        hierarchyDataRef.current = formatted;
        hierarchyFetched.current = true;
        setHierarchyUsers(formatted);
        return formatted;
      })
      .catch((err) => {
        console.error('Error fetching hierarchy for search:', err);
        hierarchyFetched.current = false;
        hierarchyDataRef.current = [];
        setHierarchyUsers([]);
        setHierarchyLoadError('Unable to load the full team hierarchy. Direct reports are still available.');
        throw err;
      })
      .finally(() => {
        isFetchingHierarchy.current = false;
        setHierarchyUsersLoading(false);
        hierarchyLoadPromiseRef.current = null;
      });

    hierarchyLoadPromiseRef.current = promise;
    return promise;
  }, [userId, userName]);

  // Preload gerarchia in background (non blocca l'apertura del menu; i report diretti sono già disponibili)
  useEffect(() => {
    if (!userId || managedUsers.length === 0 || hierarchyFetched.current) return;
    const timer = setTimeout(() => {
      ensureHierarchyLoaded().catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [userId, managedUsers.length, ensureHierarchyLoaded]);

  const usersForFilterLookup = useMemo(() => {
    const byId = new Map();
    managedUsers.forEach((u) => byId.set(u.id, u));
    hierarchyUsers.forEach((u) => {
      if (!byId.has(u.id)) byId.set(u.id, u);
    });
    return sortUsersByDisplayName(Array.from(byId.values()));
  }, [managedUsers, hierarchyUsers]);

  const searchAutocompleteOptions = useMemo(() => {
    const directIds = new Set(managedUsers.map((u) => u.id));
    const nestedOnly = hierarchyUsers.filter((u) => !directIds.has(u.id));
    return sortUsersGroupedByPeopleManager([...managedUsers, ...nestedOnly]);
  }, [managedUsers, hierarchyUsers]);

  const allHierarchyUserCount = useMemo(
    () => (hierarchyUsers.length > 0 ? usersForFilterLookup.length : managedUsers.length),
    [usersForFilterLookup.length, hierarchyUsers.length, managedUsers.length]
  );

  const resolveUserIdsForQuery = useCallback(() => {
    if (userFilterMode === 'hierarchy') {
      return usersForFilterLookup.map((u) => u.id);
    }
    if (filterUsers.length > 0) {
      return usersForFilterLookup
        .filter((u) => filterUsers.includes(u.name || u.username))
        .map((u) => u.id);
    }
    return managedUsers.map((u) => u.id);
  }, [userFilterMode, filterUsers, usersForFilterLookup, managedUsers]);

  const buildWorklogQueryParams = useCallback(({
    limit,
    skip,
    includePagination = true,
    filterModel = gridFilterModel,
    sortModel = gridSortModel,
  } = {}) => {
    const params = [];
    if (filterStartDate) params.push(`date_from=${encodeURIComponent(filterStartDate)}`);
    if (filterEndDate) params.push(`date_to=${encodeURIComponent(filterEndDate)}`);
    if (includePagination && limit != null) params.push(`limit=${limit}`);
    if (includePagination && skip != null) params.push(`skip=${skip}`);

    const serializedGridFilters = serializeGridFilters(filterModel);
    if (serializedGridFilters) {
      params.push(`grid_filters=${encodeURIComponent(serializedGridFilters)}`);
    }

    const activeSort = sortModel?.find((item) => item?.field && item?.sort);
    if (activeSort) {
      params.push(`sort_field=${encodeURIComponent(activeSort.field)}`);
      params.push(`sort_dir=${encodeURIComponent(activeSort.sort)}`);
    }

    const userIdsArray = resolveUserIdsForQuery();
    if (userIdsArray.length > 0) {
      params.push(`user_ids=${userIdsArray.map(String).join(',')}`);
    }

    return params;
  }, [
    filterStartDate,
    filterEndDate,
    gridFilterModel,
    gridSortModel,
    resolveUserIdsForQuery,
  ]);

  // Carica solo i report diretti all'avvio
  useEffect(() => {
    if (!userId) {
      setManagedUsers([]);
      return;
    }

    if (isFetchingUsers.current) {
      return;
    }

    hasAutoSetFilterUsers.current = false;
    hierarchyFetched.current = false;
    hierarchyDataRef.current = [];
    hierarchyLoadPromiseRef.current = null;
    setHierarchyUsers([]);
    setHierarchyLoadError(null);
    setUserFilterMode('direct');
    setPendingUserFilterMode('direct');

    isFetchingUsers.current = true;

    fetchDirectReportsByPmId(userId)
      .then((directReports) => {
        const formatted = sortUsersByDisplayName(
          directReports.map((u) => ({
            ...u,
            peopleManagerName: 'Direct reports',
            peopleManagerId: userId,
            isNested: false,
          }))
        );
        setManagedUsers(formatted);
        isFetchingUsers.current = false;
      })
      .catch((err) => {
        console.error('Error fetching direct reports:', err);
        setManagedUsers([]);
        isFetchingUsers.current = false;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Pre-selezione: tutti i report diretti (filterUsers vuoto = all direct)
  useEffect(() => {
    if (managedUsers.length > 0 && !hasAutoSetFilterUsers.current) {
      setPendingFilterUsers([]);
      setPendingUserFilterMode('direct');
      hasAutoSetFilterUsers.current = true;
    }
  }, [managedUsers]);

  const fetchWorklogs = async () => {
    if (isFetchingWorklogs.current) {
      return;
    }

    const userIdsArray = resolveUserIdsForQuery();
    if (userIdsArray.length === 0) {
      setRows([]);
      setRowCount(0);
      return;
    }

    isFetchingWorklogs.current = true;
    setLoading(true);
    setError(null);

    try {
      const params = buildWorklogQueryParams({
        limit: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
      });
      const url = `${API_ENDPOINTS.WORKLOGS}?${params.join('&')}`;
      const response = await fetch(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error fetching data: ${response.status} ${response.statusText}. ${errorText || 'No additional details'}`);
      }
      const data = await response.json();
      const worklogs = excludeForecastWorklogs(data.worklogs || []);
      setRowCount(data.total || 0);
      setRows(worklogs.map((row, idx) => ({ id: row.id || idx, ...row })));
    } catch (err) {
      setError(err.message);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
      isFetchingWorklogs.current = false;
    }
  };

  useEffect(() => {
    if (!userId || managedUsers.length === 0) return;
    fetchWorklogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    filterUsers,
    filterStartDate,
    filterEndDate,
    managedUsers,
    usersForFilterLookup,
    userFilterMode,
    paginationModel.page,
    paginationModel.pageSize,
    gridFilterModel,
    gridSortModel,
  ]);

  // Tooltip per le colonne Description e Project Name
  const columnsWithDescriptionTooltip = useMemo(() => columns.map(col => {
    if (col.field === 'description' || col.field === 'project_name') {
      return {
        ...col,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} placement="top" arrow>
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%'
            }}>{params.value}</span>
          </Tooltip>
        )
      };
    }
    return col;
  }), [columns]);

  // Funzione per esportare la tabella in Excel
  const handleExportExcel = async () => {
    if (exportLoading) return;
    if (resolveUserIdsForQuery().length === 0) return;

    setExportLoading(true);
    try {
      const exportChunkSize = 1000;
      let skip = 0;
      let total = null;
      const exportRows = [];

      do {
        const params = buildWorklogQueryParams({
          limit: exportChunkSize,
          skip,
        });
        const url = `${API_ENDPOINTS.WORKLOGS}?${params.join('&')}`;
        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Export failed: ${response.status} ${response.statusText}. ${errorText || ''}`);
        }
        const data = await response.json();
        const chunk = excludeForecastWorklogs(data.worklogs || []);
        if (total == null) total = data.total ?? chunk.length;
        exportRows.push(...chunk);
        skip += exportChunkSize;
      } while (exportRows.length < total);

      if (exportRows.length === 0) return;

      const dataToExport = exportRows.map(row => {
      const obj = {};
      columns.forEach(col => {
        if (col.field === 'effort') {
          // Per la colonna effort, mantieni il valore numerico per i calcoli Excel
          obj[col.headerName || col.field] = row[col.field] || 0;
          return;
        }
        if (col.field === 'effort_hours') {
          // Calcola le ore a partire dai minuti (due decimali) - mantieni valore numerico per Excel
          const minutes = Number(row.effort || 0);
          const hours = minutes ? Math.round((minutes / 60) * 100) / 100 : 0;
          obj[col.headerName || col.field] = hours;
          return;
        }
        if (col.field === 'date') {
          // Formatta la data per Excel (YYYY-MM-DD) evitando problemi di timezone
          const dateValue = row[col.field];
          if (dateValue) {
            let formattedDate, year, month;
            
            // Usa la data direttamente se è già in formato YYYY-MM-DD
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              formattedDate = dateValue;
              [year, month] = dateValue.split('-');
            } else {
              // Altrimenti converte evitando problemi di timezone
              const date = new Date(dateValue);
              year = date.getFullYear();
              month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
            
            obj[col.headerName || col.field] = formattedDate;
            // Aggiungi colonne Month e Year
            obj['Month'] = month;
            obj['Year'] = year;
          } else {
            obj[col.headerName || col.field] = '';
            obj['Month'] = '';
            obj['Year'] = '';
          }
          return;
        }
        obj[col.headerName || col.field] = row[col.field];
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Worklogs');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    
    // Genera il nome del file con l'intervallo di date
    let fileName = 'worklogs';
    
    // Funzione per convertire YYYY-MM-DD in DDMMYYYY
    const formatDateForFileName = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${day}${month}${year}`;
    };
    
    // Funzione per pulire il nome dell'utente per il nome del file
    const cleanUserName = (userName) => {
      if (!userName) return '';
      return userName.replace(/[^a-zA-Z0-9]/g, '_'); // Sostituisce caratteri speciali con underscore
    };
    
    const userName = filterUsers.length === 1 ? cleanUserName(filterUsers[0]) : 
                     filterUsers.length > 1 ? 'Multiple_Users' : '';
    
    if (filterStartDate && filterEndDate) {
      const startFormatted = formatDateForFileName(filterStartDate);
      const endFormatted = formatDateForFileName(filterEndDate);
      if (userName) {
        fileName = `worklogs_${userName}_${startFormatted}-${endFormatted}`;
      } else {
        fileName = `Worklogs_${startFormatted}-${endFormatted}`;
      }
    } else if (filterStartDate) {
      const startFormatted = formatDateForFileName(filterStartDate);
      if (userName) {
        fileName = `worklogs_${userName}_from_${startFormatted}`;
      } else {
        fileName = `Worklogs_from_${startFormatted}`;
      }
    } else if (filterEndDate) {
      const endFormatted = formatDateForFileName(filterEndDate);
      if (userName) {
        fileName = `worklogs_${userName}_until_${endFormatted}`;
      } else {
        fileName = `Worklogs_until_${endFormatted}`;
      }
    } else if (userName) {
      fileName = `worklogs_${userName}`;
    }
    
    saveAs(blob, `${fileName}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
      window.alert(err?.message || 'Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>People Manager Tracking List</Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExportExcel}
            startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={exportLoading}
          >
            {exportLoading ? 'Exporting...' : 'Excel'}
          </Button>
        </Box>
        {/* Filtri utente gestito e intervallo date */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            loading={false}
            options={[
              { id: 'all-direct', name: 'All direct reports', isSpecial: true },
              { id: 'all-hierarchy', name: 'All reports', isSpecial: true },
              ...searchAutocompleteOptions,
            ]}
            groupBy={(option) => option.isSpecial ? '' : getPeopleManagerGroupName(option)}
            getOptionLabel={(option) => option.name || option.username || option.email}
            value={
              pendingUserFilterMode === 'custom'
                ? usersForFilterLookup.filter(u => pendingFilterUsers.includes(u.name || u.username))
                : []
            }
            onChange={(event, newValue) => {
              if (newValue.some(v => v.id === 'all-direct')) {
                setPendingUserFilterMode('direct');
                setPendingFilterUsers([]);
                return;
              }
              if (newValue.some(v => v.id === 'all-hierarchy')) {
                selectAllHierarchyPending();
                return;
              }
              setPendingUserFilterMode('custom');
              setPendingFilterUsers(newValue.map(u => u.name || u.username));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Managed Users"
                placeholder="Search users..."
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            renderGroup={(params) => {
              if (!params.group) {
                return <React.Fragment key={params.key}>{params.children}</React.Fragment>;
              }
              const groupUsers = searchAutocompleteOptions.filter(
                (u) => getPeopleManagerGroupName(u) === params.group
              );
              const groupNames = groupUsers.map(u => u.name || u.username);
              const allGroupSelected = groupNames.length > 0 && groupNames.every(n => pendingFilterUsers.includes(n));
              const someGroupSelected = groupNames.some(n => pendingFilterUsers.includes(n));
              return (
                <li key={params.key}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'grey.100',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      px: 1,
                      py: 0.25,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (allGroupSelected) {
                        setPendingFilterUsers(prev => prev.filter(n => !groupNames.includes(n)));
                      } else {
                        setPendingFilterUsers(prev => [...new Set([...prev, ...groupNames])]);
                      }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={allGroupSelected}
                      indeterminate={!allGroupSelected && someGroupSelected}
                      sx={{ p: 0.5, mr: 0.5, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                    >
                      {params.group === 'Direct reports' ? params.group : `Reports of ${params.group}`}
                    </Typography>
                  </Box>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              );
            }}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                const checked = option.id === 'all-direct'
                  ? pendingUserFilterMode === 'direct'
                  : pendingUserFilterMode === 'hierarchy';
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={checked}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'black' }}>
                      {option.name}
                    </Typography>
                    {option.id === 'all-hierarchy' && hierarchyUsersLoading && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                );
              }

              return (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2">
                      {option.name || option.username}
                      {option.isPeopleManager && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'primary.main' }}>
                          (PM)
                        </Typography>
                      )}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {option.email}
                      </Typography>
                    )}
                    {option.isNested && option.peopleManagerName && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        People Manager: {option.peopleManagerName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={() => {
              if (pendingUserFilterMode === 'hierarchy') {
                return (
                  <Typography variant="body2" color="textSecondary">
                    All reports ({allHierarchyUserCount})
                  </Typography>
                );
              }
              if (pendingUserFilterMode === 'direct') {
                return (
                  <Typography variant="body2" color="textSecondary">
                    All direct reports ({managedUsers.length})
                  </Typography>
                );
              }
              return (
                <Typography variant="body2">
                  {pendingFilterUsers.length} selected
                </Typography>
              );
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No users found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              const query = inputValue.toLowerCase();
              const filteredRegular = sortUsersGroupedByPeopleManager(regularOptions.filter(option =>
                (option.name || option.username || '').toLowerCase().includes(query) ||
                (option.email && option.email.toLowerCase().includes(query)) ||
                (option.peopleManagerName && option.peopleManagerName.toLowerCase().includes(query))
              ));
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={pendingFilterStartDate}
            onChange={e => setPendingFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={pendingFilterEndDate}
            onChange={e => setPendingFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" color="primary" onClick={applyFilters} startIcon={<FilterListIcon />}>Search</Button>
        </Box>
        {hierarchyLoadError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>{hierarchyLoadError}</Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : null}
        <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              loading={loading}
              rows={rows}
              columns={columnsWithDescriptionTooltip}
              paginationModel={paginationModel}
              onPaginationModelChange={handleMainGridPaginationChange}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              paginationMode="server"
              rowCount={rowCount}
              hideFooter={false}
              sx={{ bgcolor: 'white' }}
              filterMode="server"
              filterModel={gridFilterModel}
              onFilterModelChange={handleMainGridFilterChange}
              sortingMode="server"
              sortModel={gridSortModel}
              onSortModelChange={handleMainGridSortChange}
              disableColumnFilter={false}
              disableColumnMenu={false}
            />
        </Box>
      </Box>

    </Box>
  );
} 

